import { groupBy } from "@mnk/frontend/lib/array";
import { formatMonthDay } from "@mnk/frontend/lib/date";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import type { FlagAction } from "@mnk/shared/models/flagAction";
import type { FlagColumn } from "@mnk/shared/models/flagColumn";
import type { FlagRow } from "@mnk/shared/models/flagRow";
import type { PlaceType } from "@mnk/shared/models/placeType";
import type { RoadType } from "@mnk/shared/models/roadType";
import { useCallback, useEffect, useMemo, useState } from "react";
import { A4_HEIGHT, A4_WIDTH, IMAGE_PADDING, IMAGE_ZOOM } from "../const";
import type { KiloPost, LineItem } from "./useHataage";

export interface HataageItem {
  id: string;
  lines: LineItem[];
  position: { x: number; y: number };
  kiloPost: KiloPost;
  modifiedKiloPost?: KiloPost;
  placeType: PlaceType;
  roadType: RoadType;
  dates: string[];
  config?: {
    lineWidth?: number;
    lineColor?: string;
  };
}

type Count = {
  unknown_or_chubun: number;
  rokata: number;
  sokudo: number;
};

const COUNT_START: Record<RoadType, Count> = {
  Downbound: {
    unknown_or_chubun: 750,
    rokata: 450,
    sokudo: 150,
  },
  Upbound: {
    unknown_or_chubun: A4_HEIGHT - 750,
    rokata: A4_HEIGHT - 450,
    sokudo: A4_HEIGHT - 150,
  },
  Both: {
    unknown_or_chubun: 750,
    rokata: 450,
    sokudo: 150,
  },
};

export const COLOR_CONFIG = {
  red: "#FF0000",
  yellow: "#FFFF00",
  white: "#FFFFFF",
  black: "#000000",
};

const placeToCountKey: Record<PlaceType, keyof Count> = {
  Rokata: "rokata",
  Chubun: "unknown_or_chubun",
  Sokudo: "sokudo",
  Unknown: "unknown_or_chubun",
};

export const useFlagRowsAndActions = (quantityId: string, currentDrawingImage: DrawingImage) => {
  const { startKiloPost, endKiloPost, roadType } = currentDrawingImage;
  const [hataageItems, setHataageItems] = useState<HataageItem[]>([]);

  const { data: flagRows, isFetching: isFetchingFlagRows } = trpc.getFlagRows.useQuery({
    quantityId,
    startKiloPost,
    endKiloPost,
    roadType,
    drawingImageId: currentDrawingImage.id,
  });

  const { data: flagActions, isFetching: isFetchingFlagActions } = trpc.getFlagActions.useQuery({
    flagRowIds: flagRows?.map((flagRow: FlagRow) => flagRow.id) ?? [],
  });

  const { data: flagColumns, isFetching: isFetchingFlagColumns } = trpc.getFlagColumns.useQuery({
    quantityId,
  });

  const groupByFlagColumn = useMemo(
    () => groupBy(flagColumns ?? [], (flagColumn: FlagColumn) => flagColumn.id),
    [flagColumns],
  );

  const calcWidth = useCallback(
    (lineStartKiloPost: number, lineEndKiloPost: number) => {
      // DrawingImageの向きに合わせて線の幅を計算（符号を保持）
      const lineKiloPost = lineEndKiloPost - lineStartKiloPost;
      const imageKiloPost = endKiloPost - startKiloPost;
      return Math.floor((lineKiloPost / imageKiloPost) * A4_WIDTH * IMAGE_ZOOM);
    },
    [endKiloPost, startKiloPost],
  );

  const calcPosition = useCallback(
    (lineStartKiloPost: number, countMap: Count, flagRow: FlagRow) => {
      const countKey = placeToCountKey[flagRow.placeType];

      // X は常に KP から計算（IMAGE_ZOOM 変更に自動追従）
      const startX = A4_WIDTH * IMAGE_PADDING;
      const positionX =
        startX + ((lineStartKiloPost - startKiloPost) / (endKiloPost - startKiloPost)) * A4_WIDTH * IMAGE_ZOOM;

      // Y は保存値があれば使用、なければ定数から計算
      if (flagRow.positionY) {
        return { x: positionX, y: flagRow.positionY };
      }

      const count = countMap[countKey];
      const positionY = COUNT_START[flagRow.roadType][countKey] + (count % 3) * 150;
      countMap[countKey]++;
      return { x: positionX, y: positionY };
    },
    [endKiloPost, startKiloPost],
  );

  const isModified = useCallback((flagAction: FlagAction) => {
    return flagAction.modifiedValue != null;
  }, []);

  const getLineItem = useCallback(
    (flagAction: FlagAction) => {
      const result: LineItem[] = [];
      const flagColumn = groupByFlagColumn[flagAction.flagColumnId][0];

      const noteProps = flagAction.note
        ? {
            noteContent: flagAction.note,
            noteConfig: { textColor: COLOR_CONFIG.black, backgroundColor: COLOR_CONFIG.white },
          }
        : {};

      if (flagColumn?.name != null && flagAction.value == null && isModified(flagAction)) {
        result.push({
          content: `${flagColumn.name}: ${flagAction.modifiedValue} ${flagColumn.unit ?? ""}`,
          config: { textColor: COLOR_CONFIG.red, backgroundColor: COLOR_CONFIG.white },
          ...noteProps,
        });
        return result;
      }
      if (flagColumn?.name != null && flagAction.value != null) {
        const config = isModified(flagAction)
          ? { textColor: COLOR_CONFIG.black, backgroundColor: COLOR_CONFIG.yellow }
          : { textColor: COLOR_CONFIG.black, backgroundColor: COLOR_CONFIG.white };
        const item: LineItem = {
          content: `${flagColumn.name}: ${flagAction.value} ${flagColumn.unit ?? ""}`,
          config,
        };
        if (isModified(flagAction)) {
          item.modifiedContent = `${flagAction.modifiedValue} ${flagColumn.unit ?? ""}`;
          item.modifiedConfig = { textColor: COLOR_CONFIG.red, backgroundColor: COLOR_CONFIG.white };
        }
        if (flagAction.note) {
          item.noteContent = flagAction.note;
          item.noteConfig = { textColor: COLOR_CONFIG.black, backgroundColor: COLOR_CONFIG.white };
        }
        result.push(item);
      }

      return result;
    },
    [groupByFlagColumn, isModified],
  );

  useEffect(() => {
    if (flagRows && flagActions) {
      const hataageItems: HataageItem[] = [];
      const count: Count = {
        unknown_or_chubun: 0,
        rokata: 0,
        sokudo: 0,
      };

      for (const flagRow of flagRows) {
        if (
          flagRow.modifiedStartKiloPost != null &&
          flagRow.modifiedEndKiloPost != null &&
          flagRow.startKiloPost != null &&
          flagRow.endKiloPost != null
        ) {
          const lineWidth = calcWidth(flagRow.modifiedStartKiloPost, flagRow.modifiedEndKiloPost);
          const position = calcPosition(flagRow.modifiedStartKiloPost, count, flagRow);

          hataageItems.push({
            id: `modified-${flagRow.id}`,
            modifiedKiloPost: {
              startKiloPost: flagRow.modifiedStartKiloPost,
              endKiloPost: flagRow.modifiedEndKiloPost,
              config: { textColor: COLOR_CONFIG.red, backgroundColor: COLOR_CONFIG.white },
            },
            kiloPost: {
              startKiloPost: flagRow.startKiloPost,
              endKiloPost: flagRow.endKiloPost,
              config: { textColor: COLOR_CONFIG.black, backgroundColor: COLOR_CONFIG.yellow },
            },
            placeType: flagRow.placeType,
            roadType: flagRow.roadType,
            dates: [flagRow.dateFirst, flagRow.dateSecond, flagRow.dateThird, flagRow.dateFourth]
              .filter((date): date is Date => date != null)
              .map(formatMonthDay),
            lines: flagActions
              .filter((flagAction: FlagAction) => flagAction.flagRowId === flagRow.id)
              .flatMap((flagAction: FlagAction) => getLineItem(flagAction))
              .filter((value: LineItem): value is LineItem => value != null),
            position,
            config: { lineWidth, lineColor: COLOR_CONFIG.red },
          });
          continue;
        }

        if (
          flagRow.modifiedStartKiloPost != null &&
          flagRow.modifiedEndKiloPost != null &&
          flagRow.startKiloPost == null
        ) {
          const lineWidth = calcWidth(flagRow.modifiedStartKiloPost, flagRow.modifiedEndKiloPost);
          const position = calcPosition(flagRow.modifiedStartKiloPost, count, flagRow);

          hataageItems.push({
            id: `modified-${flagRow.id}`,
            kiloPost: {
              startKiloPost: flagRow.modifiedStartKiloPost,
              endKiloPost: flagRow.modifiedEndKiloPost,
              config: { textColor: COLOR_CONFIG.red, backgroundColor: COLOR_CONFIG.white },
            },
            placeType: flagRow.placeType,
            roadType: flagRow.roadType,
            dates: [flagRow.dateFirst, flagRow.dateSecond, flagRow.dateThird, flagRow.dateFourth]
              .filter((date): date is Date => date != null)
              .map(formatMonthDay),
            lines: flagActions
              .filter((flagAction: FlagAction) => flagAction.flagRowId === flagRow.id)
              .flatMap((flagAction: FlagAction) => getLineItem(flagAction))
              .filter((value: LineItem): value is LineItem => value != null),
            position,
            config: { lineWidth, lineColor: COLOR_CONFIG.red },
          });
          continue;
        }

        if (flagRow.startKiloPost != null && flagRow.endKiloPost != null) {
          const lineWidth = calcWidth(flagRow.startKiloPost, flagRow.endKiloPost);
          const position = calcPosition(flagRow.startKiloPost, count, flagRow);
          const kiloPostModified = flagRow.modifiedStartKiloPost && flagRow.modifiedEndKiloPost;
          hataageItems.push({
            id: flagRow.id,
            kiloPost: {
              startKiloPost: flagRow.startKiloPost,
              endKiloPost: flagRow.endKiloPost,
              config: {
                textColor: COLOR_CONFIG.black,
                backgroundColor: kiloPostModified ? COLOR_CONFIG.yellow : COLOR_CONFIG.white,
              },
            },
            placeType: flagRow.placeType,
            roadType: flagRow.roadType,
            dates: [flagRow.dateFirst, flagRow.dateSecond, flagRow.dateThird, flagRow.dateFourth]
              .filter((date): date is Date => date != null)
              .map(formatMonthDay),
            lines: flagActions
              .filter((flagAction: FlagAction) => flagAction.flagRowId === flagRow.id)
              .flatMap((flagAction: FlagAction) => getLineItem(flagAction))
              .filter((value: LineItem): value is LineItem => value != null),
            position,
            config: { lineWidth },
          });
        }
      }

      setHataageItems(hataageItems);
    }
  }, [calcPosition, calcWidth, flagRows, flagActions, getLineItem]);

  const isLoading = isFetchingFlagRows || isFetchingFlagActions || isFetchingFlagColumns;

  return {
    hataageItems,
    setHataageItems,
    isLoading,
  };
};
